import type { CreateEventResponse, GuestSeedOutcome } from "@/lib/api/clients/EventClient";

export type GuestSeedToast = {
  /** i18n key inside the `createEvent` namespace. */
  key: string;
  values?: Record<string, string | number>;
  type: "success" | "error" | "info";
};

/**
 * Message selection for the best-effort guest seeds riding the apply
 * response (guest clone from a source event + guestImport CRM import).
 * Extracted from CreateEventPage so it is unit-testable without rendering
 * the wizard. Returns at most one toast per seed, clone first (matches the
 * server-side execution order).
 */
export function formatGuestSeedToasts(
  result: Pick<CreateEventResponse, "crmImport" | "guestClone" | "droppedOnClone">,
): GuestSeedToast[] {
  const toasts: GuestSeedToast[] = [];
  // What the clone had to change to make the copy fit THIS event. Announced
  // because a silent drop is the failure the destination-side checks exist to
  // end: a computed column whose operand does not exist here, or a calculated
  // automation row reading a value that is IT-04 protected here, is removed —
  // and the organiser would otherwise find out when the column stays empty or
  // the automation quietly stops calculating.
  const dropped = result.droppedOnClone ?? [];
  if (dropped.length > 0) {
    toasts.push({
      key: "clone.droppedCalculations",
      // ⚠ `{names}` is the ONLY placeholder, and the sentence is worded so it
      // reads correctly for one drop as well as for twenty. The first version
      // also passed a `count` that NO translation used — measured: 0 of 57 —
      // and adding a real ICU plural would mean getting one/few/many right by
      // hand in 57 locales. A count-neutral heading is the honest shape.
      //
      // Capped, because the list is unbounded: a template applied to an event
      // that shares no field names can drop dozens, and a bare join would then
      // be a several-hundred-character toast. The overflow is a plain number,
      // so it needs no translation; the full list is in the server log.
      values: { names: summariseDrops(dropped) },
      // No "warning" level in this toast contract — "info" is the honest one:
      // nothing FAILED, the clone simply had to change something.
      type: "info",
    });
  }
  const clone = formatOne(result.guestClone, "guestClone");
  if (clone) toasts.push(clone);
  const crm = formatOne(result.crmImport, "crmImport");
  if (crm) toasts.push(crm);
  return toasts;
}

/** At most five names, then a bare `+N` — language-neutral by construction. */
const MAX_DROP_NAMES = 5;

function summariseDrops(dropped: string[]): string {
  if (dropped.length <= MAX_DROP_NAMES) return dropped.join(", ");
  return `${dropped.slice(0, MAX_DROP_NAMES).join(", ")} (+${dropped.length - MAX_DROP_NAMES})`;
}

function formatOne(
  outcome: GuestSeedOutcome | null | undefined,
  prefix: "guestClone" | "crmImport",
): GuestSeedToast | null {
  if (!outcome) return null;
  if (outcome.errorCode) {
    // Partial results still surface the created count — the event exists
    // and some guests may already have landed before the failing chunk.
    return {
      key: `${prefix}.failed_${outcome.errorCode}`,
      values: { created: outcome.created, requested: outcome.requested },
      type: "error",
    };
  }
  if (outcome.truncated) {
    return {
      key: `${prefix}.truncated`,
      values: { created: outcome.created, requested: outcome.requested },
      type: "info",
    };
  }
  if (outcome.created === 0 && outcome.skipped === 0) {
    return { key: `${prefix}.empty`, type: "info" };
  }
  return {
    key: `${prefix}.success`,
    values: { created: outcome.created, skipped: outcome.skipped },
    type: "success",
  };
}
